import { BRAND, SITE_ORIGIN } from "../brand";
import { marketingLinks, MarketingShell, socialImageMeta } from "../components/marketing/layout";

export const links = marketingLinks;

export const meta = () => [
  { title: `${BRAND.name} Terms of Service for Shopify` },
  { name: "description", content: `Terms of service for ${BRAND.name}, a B2B quote/PO/AI counter-offer Shopify app from Legitimate LLC.` },
  { name: "robots", content: "index,follow" },
  { property: "og:type", content: "website" },
  { property: "og:url", content: `${SITE_ORIGIN}/terms` },
  { tagName: "link", rel: "canonical", href: `${SITE_ORIGIN}/terms` },
  ...socialImageMeta(),
];

export default function Terms() {
  return (
    <MarketingShell>
      <section className="qd-section qd-legal">
        <p className="qd-eyebrow">Legal</p>
        <h1 style={{ marginTop: "0.8rem" }}>Terms of Service — {BRAND.name}</h1>
        <p className="qd-legal__lede">Last updated: 2026-05-19</p>

        <h2>1. Agreement</h2>
        <p>
          By installing or using {BRAND.name} ("the app"), you ("merchant") agree to these Terms. The app is operated by
          Legitimate LLC ("we", "our", "us"). If you do not agree, do not install or continue using the app.
        </p>

        <h2>2. The service</h2>
        <p>
          {BRAND.name} provides software-as-a-service for managing B2B quote workflows on Shopify, including: quote creation
          and delivery; negotiated per-quote pricing on top of Shopify's native B2B; PO PDF parsing to draft orders; AI counter-
          offer drafting from order history; and pricing-rule verification against historical orders.
        </p>

        <h2>3. Subscriptions and billing</h2>
        <ul>
          <li>Subscriptions are billed monthly through Shopify Billing. We do not bill you directly.</li>
          <li>All paid tiers include a 14-day free trial. You will not be charged until the trial expires.</li>
          <li>You may upgrade, downgrade, or cancel at any time from within Shopify or the app's settings page.</li>
          <li>Refunds are handled by Shopify Billing's pro-rated cancellation policy.</li>
        </ul>

        <h2>4. Acceptable use</h2>
        <p>You agree not to:</p>
        <ul>
          <li>Use the app to send spam, harass buyers, or violate any applicable law.</li>
          <li>Reverse-engineer, decompile, or attempt to extract source code from the app or its infrastructure.</li>
          <li>Resell access to the app without our written consent.</li>
          <li>Upload PO PDFs containing data you do not have legal authorization to process (e.g., third-party confidential information).</li>
        </ul>

        <h2>5. AI-generated content</h2>
        <p>
          Counter-offer drafts and PO-parsing outputs are AI-generated suggestions. They may contain errors, omissions, or
          hallucinations. You are responsible for reviewing every AI output before sending it to a buyer or creating a draft
          order. We do not warrant the accuracy, completeness, or fitness of AI output for any particular purpose.
        </p>

        <h2>6. Intellectual property</h2>
        <p>
          We retain all rights to the app, its source code, and our trademarks. You retain all rights to data you upload (quotes,
          PO PDFs, your B2B catalog data). You grant us a limited license to process that data solely to operate the service.
        </p>

        <h2>7. Limitation of liability</h2>
        <p>
          To the maximum extent permitted by law, our aggregate liability for any claim arising out of or related to the app is
          limited to the amount you paid us in the twelve (12) months preceding the claim. We are not liable for indirect,
          incidental, special, consequential, or punitive damages. Some jurisdictions do not allow this limitation; in those
          jurisdictions, our liability is limited to the maximum extent permitted by law.
        </p>

        <h2>8. Warranties</h2>
        <p>
          The app is provided "as is" without warranty of any kind, express or implied, including warranties of merchantability,
          fitness for a particular purpose, and non-infringement.
        </p>

        <h2>9. Indemnification</h2>
        <p>
          You agree to indemnify and hold harmless Legitimate LLC and its officers, employees, and agents from any claim,
          damages, or expenses (including reasonable attorneys' fees) arising out of your use of the app in violation of these
          Terms or applicable law.
        </p>

        <h2>10. Termination</h2>
        <p>
          You may uninstall the app at any time from your Shopify admin. We may suspend or terminate your access at any time for
          violation of these Terms, with or without notice. Upon termination or uninstall, your data is retained for 30 days for
          reinstall continuity and then hard-deleted in accordance with our Privacy Policy.
        </p>

        <h2>11. Governing law</h2>
        <p>
          These Terms are governed by the laws of the State of Delaware, United States, without regard to its conflict of laws
          provisions.
        </p>

        <h2>12. Changes to Terms</h2>
        <p>
          We may revise these Terms at any time. Material changes will be communicated via email. Continued use of the app after
          a revision becomes effective constitutes acceptance of the revised Terms.
        </p>

        <h2>13. Data processing addendum (DPA)</h2>
        <p>
          For EU/UK merchants subject to GDPR, a Data Processing Addendum is incorporated by reference into these Terms.
          We act as a data processor for the personal data you upload (B2B contact emails, buyer names, quote notes); you
          are the data controller. Email <a href={`mailto:${BRAND.supportEmail}`}>{BRAND.supportEmail}</a> to request a
          countersigned DPA copy on company letterhead.
        </p>

        <h2>14. Force majeure</h2>
        <p>
          Neither party is liable for failure to perform obligations under these Terms when caused by events beyond
          reasonable control — including but not limited to Shopify platform outages, our hosting provider's outages,
          denial-of-service attacks, internet provider failures, fires, floods, or government actions.
        </p>

        <h2>15. Severability</h2>
        <p>
          If any provision of these Terms is held to be unenforceable, the remaining provisions remain in full force and
          effect. The unenforceable provision will be replaced by an enforceable provision that most closely matches the
          intent of the original.
        </p>

        <h2>16. Contact</h2>
        <p>
          Legal questions: <a href={`mailto:${BRAND.supportEmail}`}>{BRAND.supportEmail}</a><br />
          Mailing address: available on request, sent within 5 business days to verified merchants.
        </p>
      </section>
    </MarketingShell>
  );
}
